import {
  useMutationMembershipRevokeCancellationByMembershipIdAsMember,
  useMutationSubscriptionChangeUnschedule,
} from "@gymflow/api";
import { CurrentSubscriptionDTO } from "@gymflow/types";
import {
  AlertCircleIcon,
  CheckCircleBrokenIcon,
  ClockIcon,
} from "apps/portal/src/components/atoms";
import { CancelIcon } from "apps/portal/src/components/atoms/icons/CancelIcon";
import ChangeStatusIcon from "apps/portal/src/components/atoms/icons/ChangeStatusIcon";
import { CreditCard02Icon } from "apps/portal/src/components/atoms/icons/CreditCard02Icon";
import { PauseCircleIcon } from "apps/portal/src/components/atoms/icons/PauseCircleIcon";
import { PlayCircleIcon } from "apps/portal/src/components/atoms/icons/PlayCircleIcon";
import { ReversLeftIcon } from "apps/portal/src/components/atoms/icons/ReversLeftIcon";
import { ShieldIcon } from "apps/portal/src/components/atoms/icons/ShieldIcon";
import { usePortalRoutes } from "apps/portal/src/hooks";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { DateTime } from "luxon";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { StatusRow, StatusRowProps } from "./components/StatusRow";
import { formatDate } from "./utils/formatDate";

type HostedPagesProfileMembershipStatusesHeaderProps = {
  subscription: CurrentSubscriptionDTO;
};

export const HostedPagesProfileMembershipStatusesHeader = ({
  subscription: {
    id,
    status,
    pendingStartDate,
    scheduledChangeDate,
    scheduledChangeToMembershipName,
    pauseStartDate,
    cancellationDate,
    pauseEndDate,
    invoiceNumber,
  },
}: HostedPagesProfileMembershipStatusesHeaderProps) => {
  const { t } = useTranslation();
  const {
    club,
    properties: { gymOpeningDate },
  } = useClubSettings();
  const { api } = useGymflowModels();
  const { toast } = useContext(ToastContext);
  const history = useHistory();
  const { createSiteOrEmbedLink } = usePortalRoutes();

  const onSuccess = (toastText: string) => {
    toast({
      message: toastText,
      intent: "success",
      type: "small",
    });
  };

  const { mutateAsync: mutateAsyncSubscriptionChangeUnschedule } =
    useMutationSubscriptionChangeUnschedule({
      api,
      subscriptionId: id,
      onSuccess: () =>
        onSuccess(
          t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.toasts.changingUndone",
          ),
        ),
    });
  const { mutateAsync: mutateAsyncMembershipRevokeCancellationByMembershipId } =
    useMutationMembershipRevokeCancellationByMembershipIdAsMember({
      api,
      opts: { subscriptionId: id },
      onSuccess: () =>
        onSuccess(
          t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.toasts.cancellingUndone",
          ),
        ),
    });

  const showScaModal = useScaModal({
    asMember: true,
  });

  const isPending =
    pendingStartDate &&
    DateTime.fromISO(gymOpeningDate, { zone: club.timezone }).startOf("day") <=
      DateTime.now().setZone(club.timezone).startOf("day");

  const rows: Partial<
    Record<CurrentSubscriptionDTO["status"], StatusRowProps | undefined>
  > = {
    PENDING: isPending
      ? {
          icon: (
            <ClockIcon
              className="size-5"
              pathClassName="stroke-lightblue-500 dark:stroke-lightblue-200"
            />
          ),
          colorClass: {
            iconClassName:
              "border-lightblue-50 dark:border-lightblue-900 bg-lightblue-100 dark:bg-lightblue-700",
            textClassName: "text-lightblue-500 dark:text-lightblue-200",
          },
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.startsOn",
            { pendingStartDate: formatDate(club.timezone, pendingStartDate) },
          ),
          actionButton: {
            label: t(
              "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.changeDate",
            ),
            icon: (
              <PlayCircleIcon
                className="size-5"
                pathClassName="dark:stroke-gray-25 stroke-gray-950"
              />
            ),
            onClick: async () => {
              history.push(
                createSiteOrEmbedLink(RouteFeature.SiteProfileChangeStartDate),
              );
            },
          },
        }
      : undefined,
    ACTIVE_CHANGING:
      scheduledChangeDate && scheduledChangeToMembershipName
        ? {
            icon: (
              <ChangeStatusIcon
                className="size-5"
                pathClassName="fill-warning-700 dark:fill-warning-200"
              />
            ),
            colorClass: {
              iconClassName:
                "border-warning-50 dark:border-warning-900 bg-warning-100 dark:bg-warning-800",
              textClassName: "text-warning-600 dark:text-warning-200",
            },
            title: t(
              "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.changingOn",
              {
                scheduledChangeDate: formatDate(
                  club.timezone,
                  scheduledChangeDate,
                ),
              },
            ),
            subtitle: t(
              "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.to",
              { scheduledChangeToMembershipName },
            ),
            actionButton: {
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.undo",
              ),
              icon: (
                <ReversLeftIcon
                  className="size-5"
                  pathClassName="dark:stroke-gray-25 stroke-gray-950"
                />
              ),
              onClick: async () => {
                await mutateAsyncSubscriptionChangeUnschedule();
              },
            },
          }
        : undefined,
    ACTIVE_PAUSING: pauseStartDate
      ? {
          icon: (
            <PauseCircleIcon
              className="size-5"
              pathClassName="stroke-warning-700 dark:stroke-warning-200"
            />
          ),
          colorClass: {
            iconClassName:
              "border-warning-50 dark:border-warning-900 bg-warning-100 dark:bg-warning-800",
            textClassName: "text-warning-600 dark:text-warning-200",
          },
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.pausingOn",
            { pauseStartDate: formatDate(club.timezone, pauseStartDate) },
          ),
        }
      : undefined,
    ACTIVE_CANCELLING: cancellationDate
      ? {
          icon: (
            <CancelIcon
              className="size-5"
              pathClassName="stroke-error-600 dark:stroke-error-200"
            />
          ),
          colorClass: {
            iconClassName:
              "border-error-50 dark:border-error-900 bg-error-100 dark:bg-error-800",
            textClassName: "text-error-600 dark:text-error-200",
          },
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.cancellingOn",
            { cancellationDate: formatDate(club.timezone, cancellationDate) },
          ),
          actionButton: {
            label: t(
              "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.undo",
            ),
            icon: (
              <ReversLeftIcon
                className="size-5"
                pathClassName="dark:stroke-gray-25 stroke-gray-950"
              />
            ),
            onClick: async () => {
              await mutateAsyncMembershipRevokeCancellationByMembershipId();
            },
          },
        }
      : undefined,
    PAUSED: pauseEndDate
      ? {
          icon: (
            <PlayCircleIcon
              className="size-5"
              pathClassName="stroke-warning-700 dark:stroke-warning-200"
            />
          ),
          colorClass: {
            iconClassName:
              "border-warning-50 dark:border-warning-900 bg-warning-100 dark:bg-warning-800",
            textClassName: "text-warning-600 dark:text-warning-200",
          },
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.resumingOn",
            { pauseEndDate: formatDate(club.timezone, pauseEndDate) },
          ),
        }
      : undefined,
    OVERDUE_AUTH_NEEDED: invoiceNumber
      ? {
          icon: (
            <ShieldIcon
              className="size-5"
              pathClassName="stroke-warning-700 dark:stroke-warning-200"
            />
          ),
          colorClass: {
            iconClassName:
              "border-warning-50 dark:border-warning-900 bg-warning-100 dark:bg-warning-800",
            textClassName: "text-warning-600 dark:text-warning-200",
          },
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.authorizationNeeded",
          ),
          subtitle: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.approveToPreventDisruption",
          ),
          actionButton: {
            label: t(
              "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.approve",
            ),
            icon: (
              <CheckCircleBrokenIcon
                className="size-5"
                pathClassName="dark:stroke-gray-25 stroke-gray-950"
              />
            ),
            onClick: () => showScaModal({ invoiceNumber }),
          },
        }
      : undefined,
    OVERDUE_PAYMENT: invoiceNumber
      ? {
          icon: (
            <AlertCircleIcon
              className="size-5"
              pathClassName="stroke-error-600 dark:stroke-error-200"
            />
          ),
          colorClass: {
            iconClassName:
              "border-error-50 dark:border-error-900 bg-error-100 dark:bg-error-800",
            textClassName: "text-error-600 dark:text-error-200",
          },
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.paymentIsOverdue",
          ),
          subtitle: t(
            "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.updateInfoToPreventDisruption",
          ),
          actionButton: {
            label: t(
              "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.hostedPagesProfileMembershipStatusesHeader.pay",
            ),
            icon: (
              <CreditCard02Icon
                className="size-5"
                pathClassName="dark:stroke-gray-25 stroke-gray-950"
              />
            ),
            onClick: () => {
              history.push(
                createSiteOrEmbedLink(
                  RouteFeature.SiteOverduePayment.replace(
                    ":invoiceNumber",
                    invoiceNumber,
                  ),
                ),
              );
            },
          },
        }
      : undefined,
  };

  const config = rows[status];

  if (!config) return null;

  return <StatusRow {...config} />;
};
